/**
 * @file The ProfileComponent shows user or recipient properties in the overlay
 * @author Maarten Lensink
 */
import * as tslib_1 from "tslib";
import { OnInit, EventEmitter } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { OverlayService } from '../../core/overlay.service';
import { LocalisationService } from '../../core/localisation.service';
import { TranslateService } from '@ngx-translate/core';
import { ImageService } from '../../base/image.service';
import { EntityQuery } from 'breeze-client';
import * as moment from 'moment';
import { DomainService } from '../../domain.service';
import { AlertsService } from '../../core/alerts.service';
import { UserService } from '../../user.service';
import { OverlayProvider } from './../overlay-provider';
import { AbstractBase } from '../../core/helpers/abstract-base';
/**
 * The ProfileComponent shows user or recipient properties in the overlay.
 * @export
 * @class ProfileComponent
 * @implements {OnInit}
 */
var ProfileComponent = /** @class */ (function (_super) {
    tslib_1.__extends(ProfileComponent, _super);
    /**
     * Creates an instance of ProfileComponent
     */
    function ProfileComponent(userService, translateService, localisationService, overlayService, domainService, alertsService, imageService, overlayProvider) {
        var _this = _super.call(this) || this;
        _this.userService = userService;
        _this.translateService = translateService;
        _this.localisationService = localisationService;
        _this.overlayService = overlayService;
        _this.domainService = domainService;
        _this.alertsService = alertsService;
        _this.imageService = imageService;
        /** Event that emits when an entity is saved */
        _this.itemSaved = new EventEmitter();
        /**
         * True if the resource's expertise has not been loaded yet
         */
        _this.loadingExpertise = true;
        /**
         * True if the resource's locality has not been loaded yet
         */
        _this.loadingLocality = true;
        /**
         * True if the resource's memberships has not been loaded yet
         */
        _this.loadingMemberships = true;
        /**
         * The form group wraps the input controls
         * @memberOf ProfileComponent
         */
        _this.form = new FormGroup({});
        /**
         * Does the user have HTML5 role Employee?
         * @memberOf ProfileComponent
         */
        _this.userIsEmployee = true;
        /**
         * A list of the resource's competences
         */
        _this.competences = [];
        _this.nrVisibleCompetences = 10;
        /**
         * A list of the resource's regions
         */
        _this.regions = [];
        _this.nrVisibleRegions = 10;
        /**
         * A list of the resource's teams
         */
        _this.teams = [];
        _this.nrVisibleTeams = 10;
        _this.config = overlayProvider.configuration;
        return _this;
    }
    Object.defineProperty(ProfileComponent.prototype, "loading", {
        /**
         * Gets whether expertise, locality or memberships are still being loaded
         * @readonly
         * @type {boolean}
         */
        get: function () {
            return this.loadingExpertise || this.loadingLocality || this.loadingMemberships;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Sets the resource to be displayed
     */
    ProfileComponent.prototype.ngOnInit = function () {
        var params = this.overlayService.getParams();
        if (!params || !params.entity) {
            return;
        }
        this.userIsEmployee = (this.userService.html5role === 'employee');
        if (params.entity.entityType.shortName === 'Recipient') {
            this.getResource(params);
            return;
        }
        this.resource = params.entity;
        if (params.entity.entityType.shortName === 'Resource') {
            this.domainService.getEntityByKey(this.resource.entityType, this.resource['Oid'], false, 'City, Calendar.Base');
        }
        this.getAttributes();
    };
    /**
     * Returns the full name based on the first and last name of the user
     * @returns {string} The user's full name
     */
    ProfileComponent.prototype.fullName = function () {
        if (this.resource) {
            return this.resource['FirstName'] + ' ' + this.resource['Prefix'] + ' ' + this.resource['Name'];
        }
        return '';
    };
    /**
     * Returnt the title of the user
     *
     * @returns {string}
     */
    ProfileComponent.prototype.title = function () {
        if (this.resource) {
            return this.resource['Title'];
        }
        return '';
    };
    /**
     * Gets a promise with the profile picture url
     */
    ProfileComponent.prototype.pictureUrl = function () {
        var _this = this;
        return new Promise(function (resolve, reject) {
            return _this.imageService.getFile(_this.resource).then(function (upload) {
                resolve(upload.url);
            });
        });
    };
    /**
     * Returns a string representing the birthday, or a text explaining no birthday is known
     * @returns {string} A string representing the (absence of a) birthday
     */
    ProfileComponent.prototype.birthdayString = function () {
        var birthday = moment(this.resource['Birthday']);
        if (moment.isMoment(birthday)) {
            return birthday.format(this.localisationService.currentLanguage.formats.agenda.week.start);
        }
        return this.translateService.instant('APP.SOCIAL.NO_BIRTHDAY');
    };
    /**
     * Gets the resource to be displayed.
     * @private
     * @param {OverlayParameters} params Contains the OID of the user.
     * @memberOf ProfileComponent
     */
    ProfileComponent.prototype.getResource = function (params) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var entityType, user;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        entityType = this.domainService.getEntityType('User');
                        return [4 /*yield*/, this.domainService.getEntityByKey(entityType, params.entity['UserOid'], true, 'City, Calendar.Base').toPromise()];
                    case 1:
                        user = _a.sent();
                        this.resource = user['Resource'];
                        this.getAttributes();
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Retrieves the resource's competences, regions and teams
     * @private
     */
    ProfileComponent.prototype.getAttributes = function () {
        var _this = this;
        this.competences = [];
        this.regions = [];
        this.teams = [];
        var dataSource = {
            functionsOn: 'ResourceFunctionsOn',
            locationsOn: 'ResourceLocationsOn',
            teamsOn: 'ResourceTeamsOn'
        };
        var parameters = {
            className: 'Resource',
            oid: this.resource.Oid,
            view: new Date()
        };
        var expertiseQuery = this.getQuery(dataSource.functionsOn);
        expertiseQuery.parameters = parameters;
        this.domainService.entityManager.executeQuery(expertiseQuery).then(function (response) {
            response.results.forEach(function (link) {
                _this.competences.push(link['Child']['Name']);
            });
            _this.loadingExpertise = false;
        });
        var localityQuery = this.getQuery(dataSource.locationsOn);
        localityQuery.parameters = parameters;
        this.domainService.entityManager.executeQuery(localityQuery).then(function (response) {
            response.results.forEach(function (link) {
                _this.regions.push(link['Child']['Name']);
            });
            _this.loadingLocality = false;
        });
        var membershipQuery = this.getQuery(dataSource.teamsOn);
        membershipQuery.parameters = parameters;
        this.domainService.entityManager.executeQuery(membershipQuery).then(function (response) {
            response.results.forEach(function (link) {
                _this.teams.push(link['Child']['Name']);
            });
            _this.loadingMemberships = false;
        });
    };
    ProfileComponent.prototype.getQuery = function (source) {
        var fields = this.config.data.fields;
        var thisField = fields.find(function (field) { return field.format.dataSource === source; });
        var dataSource = thisField.format.dataSource;
        return EntityQuery.from(dataSource).expand('Child');
    };
    return ProfileComponent;
}(AbstractBase));
export { ProfileComponent };
